/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { Box, Flex, Text, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import Spreadsheet from 'react-spreadsheet';
import EWSLabels from '../../../ews-template-labels';
import Loading from '../../../../../../components/Loading';
import useStore from '../../../../../../store';
import { getCompanyTypes, getYear } from '../../../../utils';

const Q_REPORT = gql`
	query getEarlyWarningGroupingRiskRatioReport($year: Float!, $companyType: InsuranceInstitutionsType!, $companyId: String) {
		getEarlyWarningGroupingRiskRatioReport(year: $year, companyType: $companyType, companyId: $companyId)
	}
`;

export default function RatioNonLifeGroupingRiskSpreadSheetTable({
	year = null,
	companyType = null,
	companyId = null,
	nonLifeData = null,
	sfp = null,
	groupingRiskDataExport = () => null,
}) {
	const toast = useToast();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state?.currentLang);

	const [active, setActive] = useState(null);
	const [detail, setDetail] = useState(null);

	const [q1Cells, setQ1Cells] = useState([]);
	const [q2Cells, setQ2Cells] = useState([]);
	const [q3Cells, setQ3Cells] = useState([]);

	const ratioCells = [
		{ cells: q1Cells, setCells: setQ1Cells, index: 0 },
		{ cells: q2Cells, setCells: setQ2Cells, index: 1 },
		{ cells: q3Cells, setCells: setQ3Cells, index: 2 },
	];

	const [loadReport, { loading, error, data }] = useLazyQuery(Q_REPORT);

	useEffect(() => {
		load();
	}, [year, companyType, companyId]);

	useEffect(() => {
		if (data?.getEarlyWarningGroupingRiskRatioReport) {
			setDetail(data?.getEarlyWarningGroupingRiskRatioReport);
		}
	}, [data]);

	useEffect(() => {
		if (detail && nonLifeData && sfp) {
			quarterDetail(detail);
		}
	}, [detail, nonLifeData, sfp]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.forEach(({ message }) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					})
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	function load() {
		const variables = {
			year: getYear(year),
			companyType,
			companyId,
		};
		loadReport({ variables });
	}

	function emptyCells(labels = [], i, nonLifeData = { q2Cells: [], q3Cells: [], q4Cells: [] }) {
		const getCells = (labelCode) => {
			switch (labelCode) {
				case 'GROPR':
					return [
						Array.isArray(nonLifeData?.q4Cells?.[1]) ? nonLifeData.q4Cells[1] : [],
						Array.isArray(nonLifeData?.q4Cells?.[2]) ? nonLifeData.q4Cells[2] : [],
						Array.isArray(nonLifeData?.q4Cells?.[3]) ? nonLifeData.q4Cells[3] : [],
						Array.isArray(nonLifeData?.q4Cells?.[4]) ? nonLifeData.q4Cells[4] : [],
						Array.isArray(nonLifeData?.q4Cells?.[5]) ? nonLifeData.q4Cells[5] : [],
					];
				case 'GRLQR':
					return [
						Array.isArray(nonLifeData?.q4Cells?.[7]) ? nonLifeData.q4Cells[7] : [],
						Array.isArray(nonLifeData?.q4Cells?.[8]) ? nonLifeData.q4Cells[8] : [],
					];
				case 'GRRALR':
					return [
						Array.isArray(nonLifeData?.q4Cells?.[10]) ? nonLifeData.q4Cells[10] : [],
						Array.isArray(nonLifeData?.q4Cells?.[11]) ? nonLifeData.q4Cells[11] : [],
						Array.isArray(nonLifeData?.q4Cells?.[12]) ? nonLifeData.q4Cells[12] : [],
						Array.isArray(nonLifeData?.q4Cells?.[13]) ? nonLifeData.q4Cells[13] : [],
						Array.isArray(nonLifeData?.q4Cells?.[14]) ? nonLifeData.q4Cells[14] : [],
						Array.isArray(nonLifeData?.q4Cells?.[15]) ? nonLifeData.q4Cells[15] : [],
					];
				case 'GRPBR':
					return [
						Array.isArray(nonLifeData?.q4Cells?.[17]) ? nonLifeData.q4Cells[17] : [],
						Array.isArray(nonLifeData?.q4Cells?.[18]) ? nonLifeData.q4Cells[18] : [],
						Array.isArray(nonLifeData?.q4Cells?.[19]) ? nonLifeData.q4Cells[19] : [],
					];
				default:
					return [];
			}
		};

		const calculateScore = (labelCode, cells, i) => {
			switch (labelCode) {
				case 'GROPR':
					const [l1, l2, l3, l4, l5] = cells;
					const avgOR =
						(Number(l1[0]?.value || 0) +
							Number(l2[0]?.value || 0) +
							Number(l3[0]?.value || 0) +
							Number(l4[0]?.value || 0) +
							Number(l5[0]?.value || 0)) /
						5;
					switch (i) {
						case 0:
							return avgOR;
						case 1:
							return 1;
						case 2:
							return avgOR * 1;
						default:
							return 0;
					}
				case 'GRLQR':
					const [l6, l7] = cells;
					const avgLR = (Number(l6[0]?.value || 0) + Number(l7[0]?.value || 0)) / 2;
					switch (i) {
						case 0:
							return avgLR;
						case 1:
							return 1;
						case 2:
							return avgLR * 1;
						default:
							return 0;
					}
				case 'GRRALR':
					const [l8, l9, l10, l11, l12, l13] = cells;
					const avgRL =
						(Number(l8[0]?.value || 0) +
							Number(l9[0]?.value || 0) +
							Number(l10[0]?.value || 0) +
							Number(l11[0]?.value || 0) +
							Number(l12[0]?.value || 0) +
							Number(l13[0]?.value || 0)) /
						6;
					switch (i) {
						case 0:
							return avgRL;
						case 1:
							return 2;
						case 2:
							return avgRL * 2;
						default:
							return 0;
					}
				case 'GRPBR':
					const [l15, l16, l17] = cells;
					const avgPBR = (Number(l15[0]?.value || 0) + Number(l16[0]?.value || 0) + Number(l17[0]?.value || 0)) / 3;
					switch (i) {
						case 0:
							return avgPBR;
						case 1:
							return 1;
						case 2:
							return avgPBR * 1;
						default:
							return 0;
					}
				default:
					return 0;
			}
		};

		return labels?.map((label) => {
			const cells = getCells(label?.code);
			const score = calculateScore(label?.code, cells, i);
			const truncated = Math.trunc(score * 100) / 100;

			return [
				{
					value: i === 0 || i === 1 ? truncated.toFixed(2) : truncated.toFixed(2),
					readOnly: true,
					style: {
						fontWeight: 'bold',
						textAlign: 'center',
					},
				},
			];
		});
	}

	function quarterCells({ labels = [], quarter = null }) {
		return labels?.map((label) => {
			const labelObject = quarter?.find((e) => e?.code === label?.code);
			return labelObject?.values || [{ value: '0', readOnly: true }];
		});
	}

	function quarterDetail(detailData) {
		if (!detailData || !detailData?.labels) return;

		const currentRiskScore = detailData?.q1?.length
			? quarterCells({ labels: detailData?.labels, quarter: detailData?.q1 })
			: emptyCells(detailData?.labels, 0, nonLifeData);

		const weight = detailData?.q2?.length
			? quarterCells({ labels: detailData?.labels, quarter: detailData?.q2 })
			: emptyCells(detailData?.labels, 1, nonLifeData);

		const riskScore = detailData?.q3?.length
			? quarterCells({ labels: detailData?.labels, quarter: detailData?.q3 })
			: emptyCells(detailData?.labels, 2, nonLifeData);

		const numRows = detailData?.labels?.length;

		const normalize = (cells) => (cells?.length === numRows ? cells : [...cells, ...Array(numRows - cells?.length).fill([{ value: '', readOnly: true }])]);

		setQ1Cells(normalize(currentRiskScore));
		setQ2Cells(normalize(weight));
		setQ3Cells(normalize(riskScore));

		groupingRiskDataExport({
			labels: detailData?.labels,
			q1Cells: currentRiskScore,
			q2Cells: weight,
			q3Cells: riskScore,
		});
	}

	return (
		<Box>
			{loading && <Loading />}

			{!loading && !detail && (
				<Text color="gray.400" textAlign="center" mt={4}>
					{t('No Data')}
				</Text>
			)}

			{!loading && detail && (
				<Box className="ews-container">
					<div className="ews-1-row-head-ratio">{t('Grouping Risk Category')}</div>

					<Flex>
						{q1Cells?.length > 0 && (
							<table className="spreadsheet_custom_header">
								<thead>
									<tr style={{ height: '34px' }}>
										<td colSpan="2" className="ews-background-light-silver">
											{t('Current Risk Score')}
										</td>
									</tr>
								</thead>
							</table>
						)}

						{q2Cells?.length > 0 && (
							<table className="spreadsheet_custom_header">
								<thead>
									<tr style={{ height: '34px' }}>
										<td colSpan="2" className="ews-background-light-silver">
											{t('Weight')}
										</td>
									</tr>
								</thead>
							</table>
						)}

						{q2Cells?.length > 0 && (
							<table className="spreadsheet_custom_header">
								<thead>
									<tr style={{ height: '34px' }}>
										<td colSpan="2" className="ews-background-light-silver">
											{t('Risk Score')}
										</td>
									</tr>
								</thead>
							</table>
						)}
					</Flex>

					<div>
						<EWSLabels type="EWS" labels={detail?.labels} active={active} />
						<Flex>
							{ratioCells?.map(({ cells, setCells, index }) => {
								return cells?.length > 0 ? (
									<Spreadsheet
										key={index}
										data={cells}
										onChange={() => {
											setCells(
												cells?.reduce((pre, cur) => {
													pre.push([{ value: cur[index]?.value, readOnly: true }]);
													return pre;
												}, [])
											);
										}}
										onActivate={(e) => setActive({ ...e, column: e?.column + 0, t: 'EWS' })}
										hideRowIndicators
										hideColumnIndicators
									/>
								) : null;
							})}
						</Flex>
					</div>
				</Box>
			)}
		</Box>
	);
}
